import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Paginator } from '../models/paginator';
import { SearchOptions } from '../models/search-options.class';

@Injectable({
  providedIn: 'root',
})
/* It's a service that allows you to set a paginator object, and then it emits an
event when the paginator object changes */
export class TableService {
  private _paginator = new BehaviorSubject<Paginator | undefined>(undefined);
  paginator$ = this._paginator.asObservable();

  private _change = new BehaviorSubject<void>(undefined);
  change$ = this._change.asObservable();

  private searchOptionsChangedSubject =
    new BehaviorSubject<SearchOptions | null>(null);
  searchOptionsChanged$ = this.searchOptionsChangedSubject.asObservable();

  set searchOptions(options: SearchOptions | null | undefined) {
    this._searchOptions = options;
    this.searchOptionsChangedSubject.next(
      this._searchOptions ?? new SearchOptions()
    );
  }

  get searchOptions() {
    return this._searchOptions;
  }
  private _searchOptions: SearchOptions | null | undefined;

  /**
   * The paginator getter function returns the value of the private paginator
   * variable
   * @returns The value of the private variable _paginator.
   */
  get paginator() {
    return this._paginator.getValue();
  }

  /**
   * It returns an array of numbers from 1 to the number of pages
   * @returns An array of numbers from 1 to the number of pages.
   */
  get pagesArray() {
    const pages = this._paginator.getValue()?.pages || 1;
    const array: number[] = [];

    for (let i = 1; i <= pages; i++) {
      array.push(i);
    }

    return array;
  }

  /**
   * "If the current page is 1, then return true, otherwise return false."
   * @returns A boolean value.
   */
  get isFirstPage() {
    const p = this._paginator.getValue() as Paginator;
    return (p?.page || 1) <= 1;
  }

  /**
   * If the current page is the last page, then return true, otherwise return false.
   * @returns A boolean value.
   */
  get isLastPage() {
    const p = this._paginator.getValue() as Paginator;
    return (p?.page || 1) >= (p?.pages || 1);
  }

  /**
   * It takes a paginator object, and if the pages or page properties are not set,
   * it calculates them and sets them
   * @param {Paginator} p - Paginator - the paginator object
   */
  setPaginator(p: Paginator) {
    const paginator: Paginator = {
      ...p,
      pages: p?.pages || this.calculatePages(p.items ?? 0, p.size),
      page: p?.page || this._paginator.getValue()?.page || 1,
    };

    this._paginator.next(paginator);
  }

  /**
   * It takes a limit, creates a new paginator object, sets the limit, and then sets
   * the paginator
   * @param {number} limit - number - The number of items to show per page.
   */
  changeLimit(limit: number) {
    const p = this._paginator.getValue() as Paginator;
    p.size = limit;
    this.setPaginator(p);
  }

  /**
   * It sets the current page to 1, and then calls the `goToPage` function
   */
  goToFirstPage() {
    this.goToPage(1);
  }

  /**
   * "If the current page is not the last page, then go to the last page."
   * The first line of the function is a guard clause. It checks if the current page
   * is the last page. If it is, then the function returns immediately
   */
  goToLastPage() {
    const p = this._paginator.getValue() as Paginator;
    this.goToPage(p.pages as number);
  }

  /**
   * It takes a page number, calculates the start value, and then sets the
   * paginator
   * @param {number} page - number - the page number to go to
   */
  goToPage(page: number) {
    const p = this._paginator.getValue() as Paginator;
    p.page = page;

    this.setPaginator({ ...p });
    this._change.next();
  }

  /**
   * If the start value is less than the limit, then set the start value to 1,
   * otherwise subtract the limit from the start value
   * @param {Paginator} p - Paginator - this is the paginator object that is passed
   * in from the component.
   */
  prev() {
    const paginator = this._paginator.getValue() as Paginator;
    paginator.page = (paginator?.page || 1) - 1;
    this.setPaginator(paginator);
    this._change.next();
  }

  /**
   * It increments the start value of the paginator by the limit value, then sets the
   * paginator and emits a change event
   * @param {Paginator} p - Paginator - this is the paginator object that is passed
   * in from the component.
   */
  next() {
    const paginator = this._paginator.getValue() as Paginator;
    paginator.page = (paginator?.page || 1) + 1;
    this.setPaginator(paginator);
    this._change.next();
  }

  /**
   * It calculates the number of pages based on the number of items and the limit
   * @param {number} items - the total number of items in the collection
   * @param {number} limit - The number of items to show per page.
   * @returns The number of pages needed to display the items.
   */
  private calculatePages(items: number, limit: number) {
    const odd = items % limit === 0;
    let pages = Math.floor(items / limit);

    if (!odd) {
      ++pages;
    }

    return pages;
  }

  private _filtersChanged = new Subject<SearchOptions>();
  readonly filtersChanged$ = this._filtersChanged.asObservable();

  /** Llamar DESPUÉS de mutar searchOptions (aunque sea in-place) */
  notifyFiltersChanged(): void {
    const o = this.searchOptions ?? new SearchOptions();
    // Emitimos un “snapshot” con nuevas referencias para forzar CD sin remounts bruscos
    const snapshot: SearchOptions = {
      ...o,
      status: [...(o.status ?? [])],
      processor: [...(o.processor ?? [])],
      channel: [...(o.channel ?? [])],
    } as SearchOptions;

    this._filtersChanged.next(snapshot);
  }

  /**
   * It clears every active filter and returns the paginator to its first page,
   * keeping the page size chosen by the user. It notifies both the desktop
   * (searchOptionsChanged$) and the mobile (filtersChanged$) consumers, but
   * deliberately not change$, because that channel triggers a refetch.
   */
  reset(): void {
    this.searchOptions = new SearchOptions();

    const paginator = this._paginator.getValue();
    if (paginator) {
      this.setPaginator({ ...paginator, page: 1 });
    }

    this.notifyFiltersChanged();
  }
}
